import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { App } from 'antd';

import { getPreferences, patchPreferences } from '../services/preferences';
import { PREFERENCE_DEFAULTS } from '../config/preferenceDefs';
import { setDateFormat, setTimeFormat } from '../utils/dateDisplay';
import { isTokenValid } from '../utils';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * App-wide cache of the current user's preferences. Loaded once per session
 * (re-checked on route change so it picks up after login), merged over the
 * client defaults from preferenceDefs. setPref saves automatically with an
 * optimistic update and rollback — there is no Save button anywhere.
 */
const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
	const [serverPrefs, setServerPrefs] = useState({});
	const [settled, setSettled] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [error, setError] = useState(null);
	const requestedRef = useRef(false);
	const location = useLocation();
	const { message } = App.useApp();

	// Another account (or none) on this tab: back to the defaults, and the
	// latch re-arms — the next account must never read the last one's date
	// format, theme or unit system (utils/session.js).
	const session = useOnSessionChange(() => {
		requestedRef.current = false;
		setServerPrefs({});
		setError(null);
		setFetching(false);
		setSettled(false);
	});

	const load = useCallback(async ({ force = false } = {}) => {
		if (requestedRef.current && !force) return;
		if (!isTokenValid()) {
			// Signed out: nothing to ask (it would be a 401), and nothing
			// pending — settled, empty. The sign-in re-arms and loads.
			setSettled(true);
			return;
		}
		requestedRef.current = true;
		const startedFor = getSessionUserId();
		const stale = () => getSessionUserId() !== startedFor;
		setFetching(true);
		setError(null);
		try {
			const data = await getPreferences();
			if (!stale()) setServerPrefs(data || {});
		} catch (err) {
			if (!stale()) setError(err);
		} finally {
			if (!stale()) {
				setFetching(false);
				setSettled(true);
			}
		}
	}, []);

	// Route changes and a change of account re-check; load() no-ops until
	// there is a valid token, then runs once per account.
	useEffect(() => {
		load();
	}, [load, location.pathname, session]);

	const prefs = useMemo(() => ({ ...PREFERENCE_DEFAULTS, ...serverPrefs }), [serverPrefs]);

	// Keep the non-React dateDisplay store in sync so formatters and
	// subscribed components pick the preferences up immediately.
	useEffect(() => {
		setDateFormat(prefs.dateFormat);
	}, [prefs.dateFormat]);

	useEffect(() => {
		setTimeFormat(prefs.timeFormat);
	}, [prefs.timeFormat]);

	const setPref = useCallback(async (key, value) => {
		let previous;
		setServerPrefs(current => {
			previous = current;
			return { ...current, [key]: value };
		});
		try {
			const data = await patchPreferences({ [key]: value });
			setServerPrefs(data || {});
			return true;
		} catch (err) {
			setServerPrefs(previous);
			message.error('Could not save your preference. Please try again.');
			return false;
		}
	}, [message]);

	/**
	 * A save whose refusal the CALLER says, in its own words: no optimistic
	 * value (the server shapes what is stored — the active tag's `since`), no
	 * generic toast, and the error thrown back. The answer is the whole map,
	 * as every PATCH answers; one that lands for another account is dropped.
	 */
	const savePrefs = useCallback(async (partial) => {
		const startedFor = getSessionUserId();
		const data = await patchPreferences(partial);
		if (getSessionUserId() === startedFor) setServerPrefs(data || {});
		return data || {};
	}, []);

	const value = useMemo(() => ({
		prefs,
		setPref,
		savePrefs,
		loading: fetching || !settled,
		error,
		ensureLoaded: load,
	}), [prefs, setPref, savePrefs, fetching, settled, error, load]);

	return (
		<PreferencesContext.Provider value={value}>
			{children}
		</PreferencesContext.Provider>
	);
}

export function usePreferences() {
	const ctx = useContext(PreferencesContext);
	if (!ctx) {
		throw new Error('usePreferences must be used within a PreferencesProvider');
	}
	return ctx;
}

/** Null instead of throwing, for components rendered without the provider in tests. */
export function useOptionalPreferences() {
	return useContext(PreferencesContext);
}
